import React, { useEffect, useMemo, useState } from 'react';
import { Customer, Booking } from '../types';
import {
  Button,
  Checkbox,
  Field,
  Input,
  Modal,
  Select,
  Textarea,
} from './ui';

interface AddBookingModalProps {
  open: boolean;
  customers: Customer[];
  onClose: () => void;
  onSubmit: (booking: Booking) => void;
  initialData?: Booking | null;
}

interface FormState {
  customerId: string;
  fromDate: string;
  toDate: string;
  amountPaid: string;
  ourIncome: string;
  adults: string;
  children: string;
  infants: string;
  dogs: string;
  highChair: boolean;
  cot: boolean;
  notes: string;
  welcomeTray: string;
  checkoutIssues: string;
}

const emptyForm: FormState = {
  customerId: '',
  fromDate: '',
  toDate: '',
  amountPaid: '',
  ourIncome: '',
  adults: '2',
  children: '0',
  infants: '0',
  dogs: '0',
  highChair: false,
  cot: false,
  notes: '',
  welcomeTray: '',
  checkoutIssues: '',
};

/** Sanitize any input (UK / ISO / timestamp) to YYYY-MM-DD. */
const toISODate = (dateStr: string | undefined): string => {
  if (!dateStr) return '';
  const trimmed = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.substring(0, 10);
  const ukMatch = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (ukMatch) {
    const [, dd, mm, yyyy] = ukMatch;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  const d = new Date(trimmed);
  if (!isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }
  return '';
};

const AddBookingModal: React.FC<AddBookingModalProps> = ({
  open,
  customers,
  onClose,
  onSubmit,
  initialData,
}) => {
  const [formData, setFormData] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>(
    {}
  );

  useEffect(() => {
    if (initialData) {
      setFormData({
        customerId: initialData.customerId,
        fromDate: toISODate(initialData.fromDate),
        toDate: toISODate(initialData.toDate),
        amountPaid: String(initialData.amountPaid ?? ''),
        ourIncome: String(initialData.ourIncome ?? ''),
        adults: String(initialData.adults ?? 2),
        children: String(initialData.children ?? 0),
        infants: String(initialData.infants ?? 0),
        dogs: String(initialData.dogs ?? 0),
        highChair: initialData.highChair,
        cot: initialData.cot,
        notes: initialData.notes || '',
        welcomeTray: initialData.welcomeTrayContents || '',
        checkoutIssues: initialData.checkoutIssues || '',
      });
    } else {
      setFormData({ ...emptyForm, customerId: customers[0]?.id || '' });
    }
    setErrors({});
  }, [initialData, customers, open]);

  const nights = useMemo(() => {
    if (!formData.fromDate || !formData.toDate) return null;
    const f = new Date(formData.fromDate);
    const t = new Date(formData.toDate);
    if (isNaN(f.getTime()) || isNaN(t.getTime())) return null;
    const diff = (t.getTime() - f.getTime()) / (1000 * 60 * 60 * 24);
    return diff > 0 ? Math.round(diff) : null;
  }, [formData.fromDate, formData.toDate]);

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!formData.customerId) next.customerId = 'Choose a guest';
    const from = toISODate(formData.fromDate);
    const to = toISODate(formData.toDate);
    if (!from) next.fromDate = 'Required';
    if (!to) next.toDate = 'Required';
    if (from && to && from >= to) next.toDate = 'Must be after check-in';
    if (Number(formData.amountPaid) < 0) next.amountPaid = 'Cannot be negative';
    if (Number(formData.ourIncome) < 0) next.ourIncome = 'Cannot be negative';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const finalFrom = toISODate(formData.fromDate);
    const finalTo = toISODate(formData.toDate);
    const finalBookingDate = toISODate(
      initialData?.bookingDate || new Date().toISOString()
    );

    const booking: Booking = {
      ...(initialData?.id ? { id: initialData.id } : {}),
      customerId: formData.customerId,
      bookingDate: finalBookingDate,
      fromDate: finalFrom,
      toDate: finalTo,
      amountPaid: Number(formData.amountPaid) || 0,
      ourIncome: Number(formData.ourIncome) || 0,
      adults: Number(formData.adults) || 0,
      children: Number(formData.children) || 0,
      infants: Number(formData.infants) || 0,
      dogs: Number(formData.dogs) || 0,
      highChair: formData.highChair,
      cot: formData.cot,
      notes: formData.notes,
      welcomeTrayContents: formData.welcomeTray,
      checkoutIssues: formData.checkoutIssues,
    };
    onSubmit(booking);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={initialData ? 'Edit booking' : 'New booking'}
      description={
        nights !== null
          ? `${nights} ${nights === 1 ? 'night' : 'nights'}`
          : 'Record a guest stay.'
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="booking-form">
            {initialData ? 'Save changes' : 'Confirm booking'}
          </Button>
        </div>
      }
    >
      <form id="booking-form" onSubmit={handleSubmit} className="space-y-5">
        <Field
          label="Guest"
          htmlFor="bk-guest"
          required
          error={errors.customerId}
        >
          <Select
            id="bk-guest"
            value={formData.customerId}
            onChange={(e) =>
              setFormData({ ...formData, customerId: e.target.value })
            }
            disabled={!!initialData}
          >
            <option value="" disabled>
              Choose a guest…
            </option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.email})
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Check-in"
            htmlFor="bk-from"
            required
            error={errors.fromDate}
          >
            <Input
              id="bk-from"
              type="date"
              value={formData.fromDate}
              onChange={(e) =>
                setFormData({ ...formData, fromDate: e.target.value })
              }
            />
          </Field>
          <Field
            label="Check-out"
            htmlFor="bk-to"
            required
            error={errors.toDate}
          >
            <Input
              id="bk-to"
              type="date"
              value={formData.toDate}
              onChange={(e) =>
                setFormData({ ...formData, toDate: e.target.value })
              }
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Total paid (£)"
            htmlFor="bk-paid"
            required
            error={errors.amountPaid}
          >
            <Input
              id="bk-paid"
              type="number"
              min="0"
              step="0.01"
              value={formData.amountPaid}
              onChange={(e) =>
                setFormData({ ...formData, amountPaid: e.target.value })
              }
              placeholder="0"
            />
          </Field>
          <Field
            label="Our income (£)"
            htmlFor="bk-net"
            error={errors.ourIncome}
            hint="What you keep after platform fees"
          >
            <Input
              id="bk-net"
              type="number"
              min="0"
              step="0.01"
              value={formData.ourIncome}
              onChange={(e) =>
                setFormData({ ...formData, ourIncome: e.target.value })
              }
              placeholder="0"
            />
          </Field>
        </div>

        <Field label="Party">
          <div className="grid grid-cols-4 gap-2">
            {(
              [
                { key: 'adults', label: 'Adults' },
                { key: 'children', label: 'Children' },
                { key: 'infants', label: 'Infants' },
                { key: 'dogs', label: 'Dogs' },
              ] as const
            ).map(({ key, label }) => (
              <div key={key}>
                <p className="text-[11px] font-medium text-sand-500 mb-1">
                  {label}
                </p>
                <Input
                  type="number"
                  min="0"
                  value={formData[key]}
                  onChange={(e) =>
                    setFormData({ ...formData, [key]: e.target.value })
                  }
                />
              </div>
            ))}
          </div>
        </Field>

        <div className="rounded-xl border border-sand-200 bg-sand-50/60 p-3 flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            label="High chair"
            checked={formData.highChair}
            onChange={(v) => setFormData({ ...formData, highChair: v })}
          />
          <Checkbox
            label="Cot"
            checked={formData.cot}
            onChange={(v) => setFormData({ ...formData, cot: v })}
          />
        </div>

        <Field label="Notes" htmlFor="bk-notes">
          <Textarea
            id="bk-notes"
            rows={3}
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            placeholder="Any special requests or details…"
          />
        </Field>

        <Field label="Welcome tray contents" htmlFor="bk-welcome">
          <Input
            id="bk-welcome"
            type="text"
            value={formData.welcomeTray}
            onChange={(e) =>
              setFormData({ ...formData, welcomeTray: e.target.value })
            }
            placeholder="Honey, coffee pods, wine…"
          />
        </Field>

        {initialData && (
          <Field label="Checkout issues" htmlFor="bk-issues">
            <Textarea
              id="bk-issues"
              rows={2}
              value={formData.checkoutIssues}
              onChange={(e) =>
                setFormData({ ...formData, checkoutIssues: e.target.value })
              }
              placeholder="Damages, cleaning issues, missing items…"
            />
          </Field>
        )}
      </form>
    </Modal>
  );
};

export default AddBookingModal;
